import {
  Bell,
  Command,
  Menu,
  MessageSquare,
  PanelLeft,
  PanelTopClose,
  Search,
  LogOut,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { isDemoAuthEnabled } from '@/auth/demoProfiles';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAppStore } from './appStore';
import { DemoProfileSwitcher } from './DemoProfileSwitcher';
import { SettingsMenu } from './SettingsMenu';
import { TemplatesMenu } from '@/templates/TemplatesMenu';
import { usePanelStore } from '@/panels/panelStore';
import { WorkspaceLayoutsMenu } from './WorkspaceLayoutsMenu';
import { useSystemDateStore } from '@/systemDate/systemDateStore';

type TopBarProps = {
  /** Called when the mobile hamburger menu button is clicked. */
  onMobileMenuOpen?: () => void;
};

export function TopBar({ onMobileMenuOpen }: TopBarProps) {
  const { t } = useTranslation('common');
  const { t: ta } = useTranslation('auth');
  const { user, logout } = useAuth();
  const toggleLeftNav = useAppStore((s) => s.toggleLeftNav);
  const toggleChat = useAppStore((s) => s.toggleChat);
  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen);
  const openSurface = usePanelStore((s) => s.openSurface);
  const closeAllPanels = usePanelStore((s) => s.closeAllPanels);
  const panelCount = usePanelStore((s) => s.panels.length);
  const businessDate = useSystemDateStore((s) => s.businessDate);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <header className="border-border bg-card flex h-12 items-center justify-between border-b px-4">
      <div className="flex items-center gap-3">
        {/* Mobile: hamburger to open LeftNav sheet */}
        <button
          type="button"
          aria-label={t('mobile.openNav', { defaultValue: 'Open navigation' })}
          onClick={onMobileMenuOpen}
          className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1 md:hidden"
        >
          <Menu size={16} />
        </button>
        {/* Desktop: toggle left nav collapse */}
        <button
          type="button"
          aria-label={t('toggleLeftNav', { defaultValue: 'Toggle navigation' })}
          onClick={toggleLeftNav}
          className="text-muted-foreground hover:bg-muted hover:text-foreground hidden rounded p-1 md:flex"
        >
          <PanelLeft size={16} />
        </button>
        <span className="text-sm font-semibold">{t('appName')}</span>
      </div>

      <div className="flex items-center gap-2">
        {businessDate ? (
          <div
            className="border-border bg-background text-muted-foreground hidden rounded border px-2 py-1 text-[11px] lg:flex"
            title={t('systemDate.indicatorTitle', { date: businessDate })}
            data-testid="business-date-indicator"
          >
            {t('systemDate.indicator', { date: businessDate })}
          </div>
        ) : null}
        {/* Mobile: compact search icon instead of ⌘K pill */}
        <button
          type="button"
          aria-label={t('mobile.openSearch', { defaultValue: 'Search' })}
          onClick={() => setCommandPaletteOpen(true)}
          className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1 md:hidden"
        >
          <Search size={16} />
        </button>
        {/* Desktop: ⌘K pill */}
        <button
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          className="border-border bg-background text-muted-foreground hover:bg-muted hidden items-center gap-2 rounded-md border px-3 py-1 text-xs md:flex"
          aria-label={t('commandPalette', { defaultValue: 'Open command palette' })}
        >
          <Command size={12} />
          <kbd className="font-mono">⌘K</kbd>
        </button>
        <button
          type="button"
          aria-label={t('notifications', { defaultValue: 'Notifications' })}
          className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
        >
          <Bell size={16} />
        </button>
        {panelCount > 0 && (
          <button
            type="button"
            aria-label={t('closeAll')}
            onClick={() => {
              if (panelCount > 3) {
                setConfirmOpen(true);
              } else {
                closeAllPanels();
              }
            }}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            <PanelTopClose size={16} />
          </button>
        )}
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('closeAllConfirm.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('closeAllConfirm.description', { count: panelCount })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('closeAllConfirm.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  closeAllPanels();
                  setConfirmOpen(false);
                }}
              >
                {t('closeAllConfirm.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <WorkspaceLayoutsMenu />
        <TemplatesMenu />
        <SettingsMenu onOpenPreferences={() => openSurface('preferences', {})} />
        <button
          type="button"
          aria-label={t('toggleChat', { defaultValue: 'Toggle chat' })}
          onClick={toggleChat}
          className="text-muted-foreground hover:bg-muted hover:text-foreground hidden rounded p-1 md:flex"
        >
          <MessageSquare size={16} />
        </button>
        {user && isDemoAuthEnabled() ? (
          <DemoProfileSwitcher />
        ) : user ? (
          <div className="border-border ml-1 flex items-center gap-2 border-l pl-2">
            <span className="text-muted-foreground text-xs" title={user.sub}>
              {user.sub.slice(0, 18)}
            </span>
            <button
              type="button"
              aria-label={ta('userMenu.signOut')}
              onClick={logout}
              className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
            >
              <LogOut size={14} />
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
